<template>
    <div class="mb-8 space-y-2 max-w-7xl mx-auto">
        <div class="flex items-center justify-between bg-gray-50 py-2 px-3">
            <h4 class="text-lg text-center font-medium">{{ $t('Chat Groups') }}</h4>
            <div class="flex justify-end">
                <button
                    class="bg-blue-500 text-white border border-blue-500 hover:border-blue-600 hover:bg-white hover:text-blue-800 transition duration-150 text-sm rounded px-3 py-2"
                    @click="open = true"
                >
                    {{ $t('Create Chat Group') }}
                </button>
            </div>
        </div>

        <form class="py-2 grid grid-cols-2">
            <div>
                <label for="name" class="hidden">{{ $t('Search') }}</label>
                <v-select
                    class="flex-1 style-select"
                    @search="search"
                    placeholder="Filter by member"
                    :options="filterMemberList"
                    @input="setSelected"
                    clearable
                >
                    <template v-slot:option="option">
                        <div
                            class="w-full py-3 flex items-center space-x-3 focus:outline-none overflow-x-hidden"
                        >
                            <div
                                class="flex-shrink-0 w-12 sm:w-20 h-12 sm:h-20 rounded border-2 border-gray-200 overflow-hidden"
                            >
                                <img
                                    loading="lazy"
                                    alt="option.name"
                                    width="100%"
                                    height="100%"
                                    class="object-cover"
                                    :src="parseUrl(option.photoURL)"
                                />
                            </div>
                            <div class="flex-1 truncate">
                                <div class="flex items-center space-x-4">
                                    <h3 class="text-sm sm:text-lg font-medium ml-2">
                                        {{ option.name }}
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </template>
                </v-select>
            </div>
        </form>

        <modal :closable="true" @close="closeModal" title="Create Chat Group" :open="open">
            <div slot="content" class="flex flex-col" style="min-height:35vh;">
                <span
                    class="my-12 flex items-center justify-center w-full border border-transparent bg-green-400 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                    v-if="submitted"
                >
                    <svg
                        class="w-5 h-5 text-white mr-1"
                        fill="none"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    {{ $t('Submitted') }}</span
                >
                <form
                    v-else
                    class="flex-1 md:pt-6 md:pb-10 flex flex-col"
                    @submit.prevent="createChatGroup"
                >
                    <div>
                        <span class="uppercase tracking-widest text-xs">Members</span>
                        <v-select
                            :disabled="submitting || submitted"
                            label="name"
                            @search="searchMembers"
                            :placeholder="$t('Select Chat Members')"
                            :options="searchMemberList"
                            multiple
                            v-model="formMembers"
                            :closeOnSelect="false"
                        >
                            <template v-slot:option="option">
                                <div class="flex items-center mb-1">
                                    <img
                                        :src="parseUrl(option.photoURL)"
                                        class="object-cover h-8 w-8 rounded-full mr-1"
                                    />
                                    <span>{{ option.name }}</span>
                                </div>
                            </template>
                        </v-select>
                    </div>
                    <div class="mt-5">
                        <span class="uppercase tracking-widest text-xs">Moderators</span>
                        <v-select
                            :disabled="submitting || submitted"
                            class="style-select"
                            label="name"
                            @search="searchMembers"
                            :placeholder="$t('Select Moderators (Upto 5)')"
                            :options="searchMemberList"
                            v-model="formModerators"
                            :closeOnSelect="false"
                            multiple
                            :selectable="() => formModerators.length < 5"
                        >
                            <template v-slot:option="option">
                                <div class="flex items-center mb-1">
                                    <img
                                        :src="parseUrl(option.photoURL)"
                                        class="object-cover h-8 w-8 rounded-full mr-1"
                                    />
                                    <span>{{ option.name }}</span>
                                </div>
                            </template>
                        </v-select>
                    </div>
                    <div class="mt-5 flex flex-col space-y-1" v-if="formMembers.length > 2">
                        <span class="uppercase tracking-widest text-xs">{{
                            $t('Group Name')
                        }}</span>
                        <input v-model="formGroupName" class="py-2 px-3 form-input" />
                    </div>
                    <div class="flex-1" />
                    <div class="mt-auto">
                        <section class="space-y-1 flex flex-col">
                            <span
                                v-if="formError"
                                class="bg-red-100 text-center text-sm py-1 px-1 rounded shake text-red-900 max-w-3xl overflow-auto"
                            >
                                {{ this.formError }}
                            </span>
                        </section>
                        <button
                            class="flex items-center justify-center w-full border border-transparent hover:bg-transparent hover:border-blue-500 hover:text-gray-900 transition duration-150 font-medium text-sm text-white px-4 py-3 rounded focus:outline-none focus:shadow-outline"
                            :class="[
                                submitting || !this.formValid
                                    ? 'pointer-events-none bg-gray-200'
                                    : 'bg-blue-500',
                            ]"
                            type="submit"
                        >
                            <spinner v-if="submitting" class="text-white" />
                            <span v-else>{{ $t('Create Group') }}</span>
                        </button>
                    </div>
                </form>
            </div>
        </modal>
        <ul class="space-y-1">
            <li v-for="group in groups" :key="group.id">
                <ChatGroupCard :group="group" />
            </li>
        </ul>
        <spinner v-if="loading" />
        <div v-else>
            <div v-if="false && hasMore" class="flex justify-center">
                <button
                    @click="showMore"
                    class="border flex justify-center items-center h-10 w-40 border-gray-400 hover:border-gray-800 text-sm rounded px-3 py-2"
                >
                    <spinner v-if="loadingMore" class="text-white" />
                    <span v-else>{{ $t('Load More') }}</span>
                </button>
            </div>
            <div v-else class="hidden flex justify-center">
                <span>{{ $t('No more items to show') }}</span>
            </div>
        </div>
    </div>
</template>
<script>
import _ from 'lodash'
import api from '../lib/api'
import ChatGroupCard from '../components/ChatGroupCard.vue'
const QUICK_LIST = []
export default {
    components: {
        ChatGroupCard,
    },
    data() {
        return {
            groups: [],
            loading: true,
            loadingMore: false,
            limit: 100,
            submitting: false,
            submitted: false,
            searchMemberList: QUICK_LIST,
            formError: null,
            formGroupName: '',
            formMembers: [],
            formModerators: [],
            filterMemberList: [],
            filteredMember: null,
            offset: 0,
            hasMore: true,
            open: false,
        }
    },
    async mounted() {
        if (this.$route.query.reload === true) {
            this.$router.push('/chat-groups')
        }
        const [groups, newOffset] = await api.getChatGroups()
        this.groups = groups
        this.offset = newOffset
        this.loading = false
    },
    computed: {
        formValid() {
            return this.formMembers.length
        },
    },
    methods: {
        search: _.debounce(async function(search, loading) {
            if (!search || search === '') return
            loading(true)
            const result = await api.searchUsersByName(search)
            const makeList = list => {
                return list.map(item => {
                    return {
                        ...item,
                        label: item.name,
                        id: item.id,
                    }
                })
            }
            this.filterMemberList = makeList(result)
            loading(false)
        }, 500),
        async setSelected(value) {
            this.loading = true
            const [groups, newOffset] = await api.getChatGroups(value)
            this.groups = groups
            this.offset = newOffset
            this.loading = false
        },

        async showMore() {
            this.loadingMore = true
            const [moreGroups, newOffset] = await api.getChatGroups()
            this.groups = [...this.groups, ...moreGroups]
            this.offset = newOffset
            this.loadingMore = false
        },
        searchMembers: _.debounce(async function(search, loading) {
            if (!search || search === '') return
            loading(true)
            const users = await api.searchUsersByName(search)
            this.searchMemberList = users
            loading(false)
        }, 200),
        async createChatGroup() {
            this.submitting = true
            this.formError = null
            const payload = {
                members: this.formMembers.map(m => m.id),
                moderators: this.formModerators.map(m => m.id),
                name: this.formGroupName,
            }
            try {
                await api.createChatGroup(payload)
                this.submitting = false
                this.submitted = true
            } catch (err) {
                this.formError = err.message
                this.submitting = false
            }
        },
        closeModal() {
            this.formError = null
            this.open = false
            if (this.submitted) {
                this.$router.go()
            }
        },
        resetData() {
            this.formGivType = 'Meetup'
            this.formMember1 = null
            this.formMember2 = null
        },
        parseUrl(url) {
            if (!url) return url
            if (url.startsWith('http')) {
                return url
            }
            return `https://cdn.giv.link/${url}`
        },
    },
}
</script>
